import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';

const COLORS = {
  primary: '#374151',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  muted: '#6b7280',
  light: '#f4f4f4',
  white: '#ffffff',
  border: '#e5e7eb'
};

const InternalPage = ({ user, showGonxtBadge = true, fullPage = true }) => {
  const [testData, setTestData] = useState({
    devbot_sessions: [],
    devbot_robots: [],
    nxt_records: [],
    test_operators: [],
    excluded_stats: {
      devbot_count: 0,
      nxt_count: 0,
      test_operator_count: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('nxt');
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorSessions, setOperatorSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Filter states
  const [days, setDays] = useState(9999); // Default to all historical data
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [uniqueOperators, setUniqueOperators] = useState([]);
  const [robotFilter, setRobotFilter] = useState('all');
  const [uniqueRobots, setUniqueRobots] = useState([]);

  useEffect(() => {
    loadTestData();
  }, [days, startDate, endDate]);
  
  useEffect(() => {
    // Extract unique operators from test_operators data
    if (testData.test_operators?.operators) {
      const operators = testData.test_operators.operators.map(op => ({
        id: op.operator_id,
        name: op.operator_name,
        role: op.operator_role
      }));
      setUniqueOperators(operators);
    }
    // Extract unique robot serials from sessions and devbot robots
    const serials = new Set();
    (testData.nxt_records?.sessions || []).forEach(s => {
      if (s.robot_serial) serials.add(s.robot_serial);
    });
    (testData.devbot_robots || []).forEach(r => {
      if (r.serial) serials.add(r.serial);
    });
    setUniqueRobots([...serials].sort());
  }, [testData]);

  const loadTestData = async () => {
    setLoading(true);
    try {
      // Fetch test data from backend - this endpoint will return excluded test data
      console.log('Fetching test data from API with filters:', { days, startDate, endDate });
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (days) {
        params.days = days;
      }
      const response = await analyticsAPI.getTestData(params);
      console.log('Test data response:', response);
      if (response.data) {
        console.log('Setting test data:', response.data);
        setTestData(response.data);
      } else {
        console.warn('No data in response');
      }
    } catch (error) {
      console.error('Failed to load test data:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Show error instead of empty data
      alert(`Failed to load test data: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      setDays(null); // Clear days filter when using date range
    }
  };
  
  const handleDaysChange = (newDays) => {
    setDays(newDays);
    setStartDate('');
    setEndDate('');
  };
  
  const getFilteredSessions = () => {
    if (!testData.nxt_records?.sessions) return [];
    return testData.nxt_records.sessions.filter(s =>
      (operatorFilter === 'all' || s.operator_name === operatorFilter) &&
      (robotFilter === 'all' || s.robot_serial === robotFilter)
    );
  };

  const getFilteredDevbotRobots = () => {
    if (!testData.devbot_robots) return [];
    if (robotFilter === 'all') return testData.devbot_robots;
    return testData.devbot_robots.filter(r => r.serial === robotFilter);
  };
  
  const getFilteredOperators = () => {
    if (!testData.test_operators?.operators) return [];
    if (operatorFilter === 'all') return testData.test_operators.operators;
    return testData.test_operators.operators.filter(op => op.operator_name === operatorFilter);
  };

  const loadOperatorSessions = async (operatorId, operatorName) => {
    setSelectedOperator({ id: operatorId, name: operatorName });
    setLoadingSessions(true);
    try {
      const response = await analyticsAPI.getFleetSessions(null, null, operatorId, null, null, 365, null, null, true);
      setOperatorSessions(response.data?.sessions || []);
    } catch (error) {
      console.error('Failed to load operator sessions:', error);
      setOperatorSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const closeModal = () => {
    setSelectedOperator(null);
    setOperatorSessions([]);
  };

  const tabs = [
    { id: 'nxt', label: 'Test Role Sessions' },
    { id: 'operators', label: 'Test Operators' },
    { id: 'stats', label: 'Exclusion Stats' }
  ];

  return (
    <div style={{ minHeight: fullPage ? '100vh' : 'auto', backgroundColor: COLORS.light, padding: '32px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '600', color: COLORS.primary, margin: 0 }}>
            Internal Dashboard
          </h1>
          {showGonxtBadge && (
            <span style={{
              padding: '4px 12px',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500',
              border: '1px solid #fecaca'
            }}>
              GONXT.TECH ONLY
            </span>
          )}
        </div>
        <p style={{ color: COLORS.muted, fontSize: '14px', margin: 0 }}>
          {showGonxtBadge
            ? 'View excluded test data and debugging information. This page is only accessible to gonxt.tech email addresses.'
            : 'View excluded test/maintenance data and diagnostic session records.'}
        </p>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${COLORS.border}`
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* Quick Date Range Dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px' }}>
              Quick Range
            </label>
            <select
              value={days}
              onChange={(e) => handleDaysChange(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                color: COLORS.primary,
                backgroundColor: COLORS.white,
                cursor: 'pointer'
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
              <option value={9999}>All Time</option>
            </select>
          </div>
          
          {/* Operator Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px' }}>
              Operator Filter
            </label>
            <select
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                color: COLORS.primary,
                backgroundColor: COLORS.white,
                cursor: 'pointer'
              }}
            >
              <option value="all">All Operators</option>
              {uniqueOperators.map(op => (
                <option key={op.id} value={op.name}>
                  {op.name} ({op.role})
                </option>
              ))}
            </select>
          </div>

          {/* Robot Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px' }}>
              Robot Filter
            </label>
            <select
              value={robotFilter}
              onChange={(e) => setRobotFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                color: COLORS.primary,
                backgroundColor: COLORS.white,
                cursor: 'pointer'
              }}
            >
              <option value="all">All Robots</option>
              {uniqueRobots.map(serial => (
                <option key={serial} value={serial}>
                  {serial}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: `1px solid ${COLORS.border}`,
        paddingBottom: '8px'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab.id ? COLORS.accent : 'transparent',
              color: activeTab === tab.id ? COLORS.white : COLORS.muted,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${COLORS.border}`
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: COLORS.muted }}>
            Loading test data...
          </div>
        ) : (
          <>
            {activeTab === 'nxt' && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.primary, marginBottom: '16px' }}>
                  Test Role Sessions ({operatorFilter === 'all' && robotFilter === 'all' ? testData.nxt_records?.count || 0 : getFilteredSessions().length})
                </h2>
                <p style={{ color: COLORS.muted, fontSize: '14px', marginBottom: '16px' }}>
                  Sessions by test operators.
                </p>
                {getFilteredSessions().length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.light }}>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Operator</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Role</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Robot</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Hospital</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Ward</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Start Time</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredSessions().map((session, i) => (
                          <tr 
                            key={i} 
                            style={{ 
                              borderTop: `1px solid ${COLORS.border}`
                            }}
                          >
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.operator_name || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.operator_role || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.robot_serial || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.hospital_name || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.ward_name || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.start_time || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.duration_minutes ? `${session.duration_minutes} min` : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px', color: COLORS.muted }}>
                    No test role sessions found in the database.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'operators' && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.primary, marginBottom: '16px' }}>
                  Test Operators ({testData.test_operators?.count || 0})
                </h2>
                <p style={{ color: COLORS.muted, fontSize: '14px', marginBottom: '16px' }}>
                  Test operators.
                </p>
                {testData.test_operators?.operators?.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.light }}>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Operator Name</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Role</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Total Sessions</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Success Rate</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Robots Used</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Last Session</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredOperators().map((op, i) => (
                          <tr 
                            key={i} 
                            onClick={() => loadOperatorSessions(op.operator_id, op.operator_name)}
                            style={{ 
                              borderTop: `1px solid ${COLORS.border}`,
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.light}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            title={op.hospital_name ? `Hospitals: ${op.hospital_name}` : 'N/A'}
                          >
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.operator_name || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.operator_role || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.total_sessions || 0}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.success_rate ? `${op.success_rate}%` : 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.robots_used || 0}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{op.last_session || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px', color: COLORS.muted }}>
                    No test operators found in the database.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'stats' && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.primary, marginBottom: '16px' }}>
                  Exclusion Statistics
                </h2>
                <p style={{ color: COLORS.muted, fontSize: '14px', marginBottom: '24px' }}>
                  Summary of test data excluded from production analytics.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                  <div style={{
                    backgroundColor: COLORS.light,
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '36px', fontWeight: '700', color: COLORS.warning, margin: '0 0 8px 0' }}>
                      {testData.nxt_records?.count || 0}
                    </p>
                    <p style={{ color: COLORS.muted, fontSize: '14px', margin: 0 }}>Test Role Sessions</p>
                  </div>
                  <div style={{
                    backgroundColor: COLORS.light,
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '36px', fontWeight: '700', color: COLORS.accent, margin: '0 0 8px 0' }}>
                      {testData.test_operators?.count || 0}
                    </p>
                    <p style={{ color: COLORS.muted, fontSize: '14px', margin: 0 }}>Test Operators</p>
                  </div>
                  <div style={{
                    backgroundColor: COLORS.light,
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '36px', fontWeight: '700', color: COLORS.danger, margin: '0 0 8px 0' }}>
                      {testData.excluded_stats?.devbot_count || 0}
                    </p>
                    <p style={{ color: COLORS.muted, fontSize: '14px', margin: 0 }}>DEVBOT Sessions</p>
                  </div>
                </div>
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #fcd34d'
                }}>
                  <p style={{ color: '#92400e', fontSize: '14px', margin: 0 }}>
                    <strong>Note:</strong> These records are excluded from all production dashboards and analytics.
                    Exclusion criteria: Operators with roles IT, IT Developer, Developer, or Electrician; specific operators (Samantha Magano, Luke Templeman); and robots with DEVBOT serial numbers.
                  </p>
                </div>

                <h3 style={{ fontSize: '16px', fontWeight: '600', color: COLORS.primary, margin: '32px 0 16px 0' }}>
                  DEVBOT Robot Serials ({getFilteredDevbotRobots().length})
                </h3>
                <p style={{ color: COLORS.muted, fontSize: '14px', marginBottom: '16px' }}>
                  Robots with DEVBOT serial numbers. Visible only on this internal dashboard.
                </p>
                {getFilteredDevbotRobots().length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.light }}>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Serial</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Hospital</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Version</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Total Sessions</th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Last Session</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredDevbotRobots().map((robot, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px', fontFamily: 'monospace' }}>{robot.serial || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{robot.hospital_name || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{robot.version || 'N/A'}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{robot.total_sessions || 0}</td>
                            <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{robot.last_session || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px', color: COLORS.muted }}>
                    No DEVBOT robots found in the database.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Refresh Button */}
      <div style={{ marginTop: '24px', textAlign: 'right' }}>
        <button
          onClick={loadTestData}
          style={{
            padding: '12px 24px',
            backgroundColor: COLORS.accent,
            color: COLORS.white,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Refresh Data
        </button>
      </div>

      {/* Session Details Modal */}
      {selectedOperator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            maxWidth: '1200px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${COLORS.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: COLORS.primary, margin: '0 0 4px 0' }}>
                  Fleet Sessions - {selectedOperator.name}
                </h2>
                <p style={{ color: COLORS.muted, fontSize: '14px', margin: 0 }}>
                  {operatorSessions.length} sessions found
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: COLORS.muted,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              {loadingSessions ? (
                <div style={{ textAlign: 'center', padding: '48px', color: COLORS.muted }}>
                  Loading sessions...
                </div>
              ) : operatorSessions.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.light }}>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Robot</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Hospital</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Ward</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Bed</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operatorSessions.map((session, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.Serial || 'N/A'}</td>
                          <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.Hospital_Name || 'N/A'}</td>
                          <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.WardName || 'N/A'}</td>
                          <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.BedName || 'N/A'}</td>
                          <td style={{ padding: '12px 16px', color: COLORS.primary, fontSize: '14px' }}>{session.MachineStartTime || 'N/A'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: session.Diagnostic === 'Passed' ? '#d1fae5' : '#fef3c7',
                              color: session.Diagnostic === 'Passed' ? '#065f46' : '#92400e'
                            }}>
                              {session.Diagnostic || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px', color: COLORS.muted }}>
                  No sessions found for this operator.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalPage;
